import React, { memo, useCallback } from 'react'
import {
  ConfirmationModal,
  IConfirmationModalProps,
} from 'transfix-ui/components/Modals/ConfirmationModal'
import { useGlobalToast } from 'transfix-ui/components/Toast'
import { Typography } from 'transfix-ui/core'
import { TEST_IDS } from '../../../../constants'
import useRemoveCarrierMutation from './hooks/useRemoveCarrierMutation'

const MODAL_TITLE_SINGLE_CARRIER = 'Remove your only carrier?'
const MODAL_TITLE_MULTIPLE_CARRIER = 'Remove carrier?'

const MODAL_BODY_SINGLE_CARRIER =
  'You will lose access to the Transfix carrier platform if you do not have at least one carrier associated with your account.'
const MODAL_BODY_MULTIPLE_CARRIER =
  'You will no longer be able to view this carrier’s loads or create new loads for this carrier.'

const TOAST_SUCCESS_MESSAGE = 'Carrier removed'

export interface IRemoveCarrierConfirmationModalProps
  extends Pick<IConfirmationModalProps, 'onClose'> {
  motorCarrierId: string
  hasMultipleCarriers: boolean
  onSuccess: () => void
}

const RemoveCarrierConfirmationModal = memo<IRemoveCarrierConfirmationModalProps>(
  ({ hasMultipleCarriers, motorCarrierId, onClose, onSuccess }) => {
    const { openToast } = useGlobalToast()
    const { loading, removeCarrier } = useRemoveCarrierMutation({ motorCarrierId })

    const removeCarrierHandler = useCallback(async () => {
      const response = await removeCarrier()
      if (response) {
        openToast({ toastMessage: TOAST_SUCCESS_MESSAGE, toastType: 'default' })
        onSuccess()
      }
    }, [onSuccess, openToast, removeCarrier])

    const modalActions: IConfirmationModalProps['actions'] = [
      {
        label: 'Cancel',
        type: 'dismiss',
        disabled: loading,
        action: onClose,
      },
      {
        label: 'Remove Carrier',
        type: 'confirm',
        disabled: loading,
        action: removeCarrierHandler,
      },
    ]

    return (
      <ConfirmationModal
        title={hasMultipleCarriers ? MODAL_TITLE_MULTIPLE_CARRIER : MODAL_TITLE_SINGLE_CARRIER}
        dataTest={TEST_IDS.removeCarrierConfirmationModal}
        actions={modalActions}
        onClose={onClose}
        size={4}
        open>
        <Typography variant='body2'>
          {hasMultipleCarriers ? MODAL_BODY_MULTIPLE_CARRIER : MODAL_BODY_SINGLE_CARRIER}
        </Typography>
      </ConfirmationModal>
    )
  }
)

export default RemoveCarrierConfirmationModal
